import React from 'react';

const baseFieldClasses =
  'block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-slate-100 disabled:text-slate-400';

const errorFieldClasses = 'border-red-400 focus:ring-red-500 focus:border-red-500';

export const Label: React.FC<{ htmlFor?: string; children: React.ReactNode; required?: boolean }> = ({
  htmlFor,
  children,
  required,
}) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { hasError?: boolean }>(
  ({ className = '', hasError, ...rest }, ref) => (
    <input ref={ref} className={`${baseFieldClasses} ${hasError ? errorFieldClasses : ''} ${className}`} {...rest} />
  )
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { hasError?: boolean }
>(({ className = '', hasError, ...rest }, ref) => (
  <textarea ref={ref} className={`${baseFieldClasses} ${hasError ? errorFieldClasses : ''} ${className}`} {...rest} />
));
Textarea.displayName = 'Textarea';

export const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & { hasError?: boolean }
>(({ className = '', hasError, children, ...rest }, ref) => (
  <select ref={ref} className={`${baseFieldClasses} bg-white ${hasError ? errorFieldClasses : ''} ${className}`} {...rest}>
    {children}
  </select>
));
Select.displayName = 'Select';

export const FieldError: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  if (!children) return null;
  return <p className="mt-1 text-xs text-red-600">{children}</p>;
};

interface FormFieldProps {
  label: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  error?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export const FormField: React.FC<FormFieldProps> = ({ label, htmlFor, required, error, children, className = '' }) => (
  <div className={className}>
    <Label htmlFor={htmlFor} required={required}>
      {label}
    </Label>
    {children}
    <FieldError>{error}</FieldError>
  </div>
);

export const Checkbox: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { label: React.ReactNode }> = ({
  label,
  className = '',
  id,
  ...rest
}) => (
  <label htmlFor={id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
    <input
      id={id}
      type="checkbox"
      className={`h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 ${className}`}
      {...rest}
    />
    {label}
  </label>
);

export default FormField;
